import React, { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import CalendarDayCell from './CalendarDayCell'
import { rem } from 'polished'
import { calcGridPositionsForVerticalMonth, getCalendarMonthEmptyCells, getLocalDate } from 'lib/datetime/calendarUtils'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'

const BaseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7 , 1fr);
`

const Grid = styled(BaseGrid)`
  position: relative;
  margin-top: ${rem(8)};
`

const EmptyCell = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${rem(8)} ${rem(12)};
  background-color: ${props => props.theme.palette.neutral.default.seven};
  box-shadow: 0 -1px 0 0 ${props => props.theme.palette.neutral.default.five},
    1px 0 0 0 ${props => props.theme.palette.neutral.default.five},
    1px 0 0 0 ${props => props.theme.palette.neutral.default.five} inset,
    0 -1px 0 0 ${props => props.theme.palette.neutral.default.five} inset;
`

interface Props {
  onDatesChange: (day: App.BedbankCalendarDay) => void
  checkIn?: string
  checkOut?: string
  days: Array<App.BedbankCalendarDay>
  className?: string
  duration: number
  flights: boolean
  adultsNumber: number
  roomsNumber: number
  displayPricingAsPerPerson: boolean
}

function CalendarDaysMonth(props: Props) {
  const {
    className,
    days,
    checkIn,
    checkOut,
    duration,
    flights,
    onDatesChange,
    adultsNumber,
    roomsNumber,
    displayPricingAsPerPerson,
  } = props

  const intl = useIntl()

  const dayNames = [
    {
      short: intl.formatMessage({
        id: 'pflpryaNH/',
        defaultMessage: 'Sun',
        description: 'Abbreviated day name for Sunday in calendar header',
      }),
      full: intl.formatMessage({
        id: 'elZh/u2EPN',
        defaultMessage: 'Sunday',
        description: 'Full day name for Sunday, used as tooltip in calendar header',
      }),
    },
    {
      short: intl.formatMessage({
        id: 'MYq2j2H2Ah',
        defaultMessage: 'Mon',
        description: 'Abbreviated day name for Monday in calendar header',
      }),
      full: intl.formatMessage({
        id: 'lLcq8wwAeo',
        defaultMessage: 'Monday',
        description: 'Full day name for Monday, used as tooltip in calendar header',
      }),
    },
    {
      short: intl.formatMessage({
        id: '3wUKXX9ArW',
        defaultMessage: 'Tue',
        description: 'Abbreviated day name for Tuesday in calendar header',
      }),
      full: intl.formatMessage({
        id: 'A6V+YCo6yC',
        defaultMessage: 'Tuesday',
        description: 'Full day name for Tuesday, used as tooltip in calendar header',
      }),
    },
    {
      short: intl.formatMessage({
        id: 'o8pdOSQJkZ',
        defaultMessage: 'Wed',
        description: 'Abbreviated day name for Wednesday in calendar header',
      }),
      full: intl.formatMessage({
        id: 'Lz42L4UpKC',
        defaultMessage: 'Wednesday',
        description: 'Full day name for Wednesday, used as tooltip in calendar header',
      }),
    },
    {
      short: intl.formatMessage({
        id: 'j8tASNhaMk',
        defaultMessage: 'Thu',
        description: 'Abbreviated day name for Thursday in calendar header',
      }),
      full: intl.formatMessage({
        id: 'DO1sBh6hZA',
        defaultMessage: 'Thursday',
        description: 'Full day name for Thursday, used as tooltip in calendar header',
      }),
    },
    {
      short: intl.formatMessage({
        id: 'woBz4/hEkm',
        defaultMessage: 'Fri',
        description: 'Abbreviated day name for Friday in calendar header',
      }),
      full: intl.formatMessage({
        id: '3nwMHsNEVM',
        defaultMessage: 'Friday',
        description: 'Full day name for Friday, used as tooltip in calendar header',
      }),
    },
    {
      short: intl.formatMessage({
        id: 'HaMvQSO8j+',
        defaultMessage: 'Sat',
        description: 'Abbreviated day name for Saturday in calendar header',
      }),
      full: intl.formatMessage({
        id: 'ashmCW+uRW',
        defaultMessage: 'Saturday',
        description: 'Full day name for Saturday, used as tooltip in calendar header',
      }),
    },
  ]

  const firstDate = days[0]

  const startCellStyle = useMemo(() => ({
    gridColumnStart: getLocalDate(firstDate.checkIn).getDay() + 1,
  }), [firstDate])

  const emptyCells = useMemo(() => getCalendarMonthEmptyCells(firstDate), [firstDate])

  const betweenRange = useMemo(() => {
    if (checkIn && checkOut) {
      return calcGridPositionsForVerticalMonth(
        getLocalDate(firstDate.checkIn),
        getLocalDate(checkIn),
        getLocalDate(checkOut),
      ).range
    }
    return undefined
  }, [checkIn, checkOut, firstDate])

  const onDayClick = useCallback((day: App.BedbankCalendarDay) => {
    onDatesChange(day)
  }, [onDatesChange])

  return (
    <div className={className}>
      <BaseGrid>
        {dayNames.map((day) => (
          <Caption variant="large" as="abbr" title={day.full} key={day.full}>{day.short}</Caption>
        ))}
      </BaseGrid>
      <Grid>
        {emptyCells.map((cell, index) =>
          <EmptyCell style={{ gridColumnStart: cell.column }} key={index}>
            <Heading colour="neutral-four" variant="heading6">{cell.day}</Heading>
          </EmptyCell>,
        )}
        {days.map((day, index) => <CalendarDayCell
          key={day.checkIn}
          duration={duration}
          price={day.price}
          propertyFees={day.propertyFees}
          style={(index === 0 && emptyCells.length === 0) ? startCellStyle : undefined}
          day={day.day}
          flights={flights}
          flightPrice={day.flightPrice}
          hasPromo={day.hasPromo}
          isCorporate={day.isCorporate}
          isCheckIn={day.checkIn === checkIn}
          isCheckOut={day.checkIn === checkOut}
          isBetweenDay={!!(betweenRange && (index + 1) >= betweenRange.start && index < (betweenRange.end - 1))}
          onClick={() => onDayClick(day)}
          displayPricingAsPerPerson={displayPricingAsPerPerson}
          adultsNumber={adultsNumber}
          roomsNumber={roomsNumber}
        />)}
      </Grid>
    </div>
  )
}

export default withOfferPageState((state) => ({
  checkIn: state.checkInDate,
  checkOut: state.checkOutDate,
}), CalendarDaysMonth)
